import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSession, useOrganization } from '@clerk/clerk-react'
import { toast } from 'sonner'
import {
  agentUrl,
  conversationUrl,
  conversationMessagesUrl,
  type AgentId,
} from '../config/env'
import { ApiError, fetchJsonWithAuth } from '../lib/api'
import { conversationKeys } from '../lib/queryKeys'
import { RUN_STATUS_POLL_INTERVAL_MS } from '../config/constants'
import { toolResultStatus } from '../utils/toolConfig'
import type {
  ChatEvent,
  ConversationRunStatus,
  ConversationSummary,
  MessageAttachmentDisplay,
  MessageIntent,
} from '../types'
import type {
  Message,
  UserMessage,
  AssistantMessage,
  ToolMessage,
} from '@ag-ui/core'

// Utility function for generating conversation IDs
export const generateConversationId = () => crypto.randomUUID()

// Helper function to collect tool results
function collectToolResults(messages: Message[]): Map<string, string> {
  const toolResults = new Map<string, string>()
  for (const msg of messages) {
    if (msg.role === 'tool') {
      const toolMsg = msg as ToolMessage
      toolResults.set(toolMsg.toolCallId, toolMsg.content)
    }
  }
  return toolResults
}

// Extract plain text from UserMessage.content (which may be a string or array of content parts)
function extractTextContent(content: UserMessage['content']): string {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .filter(
        (part): part is { type: 'text'; text: string } => part.type === 'text'
      )
      .map((part) => part.text)
      .join('\n')
  }
  return String(content ?? '')
}

// Attachment descriptor from the backend's message_attachments map
interface ApiAttachment {
  mediaType: string
  // Present for marker-backed files; absent for legacy inline attachments.
  fileId?: string
  filename?: string
  url?: string
}

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// Thinking part from the backend (position-indexed relative to ag-ui messages)
export interface ThinkingPart {
  id: string
  content: string
  position: number
}

function tsFromMap(map: Record<string, string>, id: string | undefined): Date {
  const iso = id !== undefined ? map[id] : undefined
  return iso !== undefined ? new Date(iso) : new Date()
}

const TYPE_LABEL: Record<string, string> = {
  'application/pdf': 'PDF',
  'text/csv': 'CSV',
  [XLSX_MEDIA_TYPE]: 'Excel',
}

// Helper function to create chat events from messages
/** Marker-backed attachments carry their real filename; legacy inline ones
 *  do not (pydantic-ai dropped it), so those fall back to a media-type label.
 *  `url` is a signed preview for stored images, or a data URI the backend
 *  inlines for small legacy ones; without it the chip renders as a paperclip. */
function attachmentsFromApi(
  raw: ApiAttachment[] | undefined
): MessageAttachmentDisplay[] | undefined {
  if (!raw?.length) return undefined
  return raw.map((a) => ({
    mediaType: a.mediaType,
    filename: a.filename || TYPE_LABEL[a.mediaType] || 'Image',
    url: a.url,
  }))
}

export function createChatEvents(
  messages: Message[],
  toolResults: Map<string, string>,
  thinkingParts: ThinkingPart[] = [],
  messageTimestamps: Record<string, string> = {},
  messageAttachments: Record<string, ApiAttachment[]> = {},
  messageIntents: Record<string, MessageIntent> = {}
): ChatEvent[] {
  const chatEvents: ChatEvent[] = []
  const seenUserKeys = new Set<string>()

  // Index thinking parts by position for efficient lookup
  const thinkingByPosition = new Map<number, ThinkingPart[]>()
  for (const tp of thinkingParts) {
    const list = thinkingByPosition.get(tp.position) || []
    list.push(tp)
    thinkingByPosition.set(tp.position, list)
  }

  // Track message index (excluding tool messages which are skipped)
  let msgIndex = 0

  for (const msg of messages) {
    // Insert any thinking parts that should appear before this message
    const thinkingBefore = thinkingByPosition.get(msgIndex)
    if (thinkingBefore) {
      for (const tp of thinkingBefore) {
        chatEvents.push({
          id: tp.id,
          type: 'thinking_message',
          content: tp.content,
          timestamp: tsFromMap(messageTimestamps, tp.id),
          isStreaming: false,
        })
      }
    }

    if (msg.role === 'user') {
      const userMsg = msg as UserMessage
      const textContent = extractTextContent(userMsg.content)
      // Dedup on content + per-message timestamp, not content alone: a true
      // double-persist repeats the same prompt with the same timestamp (drop
      // it), but a legitimately repeated question carries a distinct timestamp
      // and must keep its own bubble.
      const normalized = textContent.trim().replace(/\s+/g, ' ').toLowerCase()
      const userTs = messageTimestamps[userMsg.id] ?? ''
      const key = normalized && `${normalized}::${userTs}`
      if (key && seenUserKeys.has(key)) {
        msgIndex++
        continue // skip a true duplicate (same text AND timestamp) in one payload
      }
      if (key) seenUserKeys.add(key)
      chatEvents.push({
        id: userMsg.id,
        type: 'user_message',
        content: textContent,
        timestamp: tsFromMap(messageTimestamps, userMsg.id),
        attachments: attachmentsFromApi(messageAttachments[userMsg.id]),
        ...(messageIntents[userMsg.id]
          ? { intent: messageIntents[userMsg.id] }
          : {}),
      })
    } else if (msg.role === 'assistant') {
      const assistantMsg = msg as AssistantMessage

      // Only add assistant message if it has actual text content
      if (assistantMsg.content && assistantMsg.content.trim()) {
        chatEvents.push({
          id: assistantMsg.id,
          type: 'assistant_message',
          content: assistantMsg.content,
          timestamp: tsFromMap(messageTimestamps, assistantMsg.id),
        })
      }

      // Add tool calls as separate standalone events (like live streaming)
      if (assistantMsg.toolCalls) {
        // Tool calls share the parent AssistantMessage timestamp
        const toolCallTimestamp = tsFromMap(messageTimestamps, assistantMsg.id)
        for (const toolCall of assistantMsg.toolCalls) {
          const result = toolResults.get(toolCall.id)

          // Parse arguments safely
          let parsedArgs: Record<string, unknown> = {}
          try {
            parsedArgs = JSON.parse(toolCall.function.arguments || '{}')
          } catch (error) {
            console.warn(
              'Failed to parse tool call arguments:',
              toolCall.function.arguments,
              error
            )
          }

          chatEvents.push({
            id: toolCall.id,
            type: 'tool_call',
            toolName: toolCall.function.name,
            toolCallId: toolCall.id,
            status: toolResultStatus(result),
            args: parsedArgs,
            result: result,
            timestamp: toolCallTimestamp,
            startTime: toolCallTimestamp,
            endTime: toolCallTimestamp,
          })
        }
      }
    }
    // Skip tool messages from event creation - they're handled above
    msgIndex++
  }

  // Insert any thinking parts that come after all messages
  const trailingThinking = thinkingByPosition.get(msgIndex)
  if (trailingThinking) {
    for (const tp of trailingThinking) {
      chatEvents.push({
        id: tp.id,
        type: 'thinking_message',
        content: tp.content,
        timestamp: tsFromMap(messageTimestamps, tp.id),
        isStreaming: false,
      })
    }
  }

  return chatEvents
}

export function useConversationsList(agentId: AgentId) {
  const { session } = useSession()
  const { organization } = useOrganization()
  const orgSlug = organization?.slug

  return useQuery({
    queryKey: conversationKeys.list(agentId, orgSlug),
    queryFn: async () => {
      const token = await session?.getToken()
      const response = await fetch(`${agentUrl(agentId)}/conversations`, {
        headers: {
          Authorization: token || '',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.statusText}`)
      }

      const data = await response.json()
      return data.conversations as ConversationSummary[]
    },
    enabled: !!session && !!orgSlug,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    // Poll while any conversation has a detached run working, so its sidebar
    // badge clears (and the title lands) without a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.run_status === 'running')
        ? RUN_STATUS_POLL_INTERVAL_MS
        : false,
  })
}

/** True only for a real "conversation not found" (backend 404), not for
 *  transient failures (network blip, 502 during deploy, stale-token 500). */
export function isConversationNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

export interface ConversationMessagesData {
  events: ChatEvent[]
  runStatus: ConversationRunStatus | null
}

export function useConversationMessages(
  agentId: AgentId,
  conversationId: string | undefined,
  /** True while this tab already watches the run's live stream — the poll
   *  would only re-download unchanged history. */
  { suspendPolling = false }: { suspendPolling?: boolean } = {}
) {
  const { session } = useSession()
  const { organization } = useOrganization()
  const orgSlug = organization?.slug

  return useQuery({
    queryKey: conversationKeys.messages(agentId, conversationId, orgSlug),
    queryFn: async (): Promise<ConversationMessagesData> => {
      if (!conversationId) return { events: [], runStatus: null }

      const token = await session?.getToken()
      // fetchJsonWithAuth throws ApiError(status) on non-OK, so 404 still
      // surfaces via isConversationNotFoundError and the retry predicate below.
      const data = await fetchJsonWithAuth<{
        messages?: Message[]
        thinking_parts?: ThinkingPart[]
        message_timestamps?: Record<string, string>
        message_attachments?: Record<string, ApiAttachment[]>
        message_intents?: Record<string, MessageIntent>
        run_status?: ConversationRunStatus | null
      }>({
        token: token || '',
        url: conversationMessagesUrl(agentId, conversationId),
        errorMessage: 'Failed to fetch conversation messages',
      })

      // Validate response structure
      if (!data.messages || !Array.isArray(data.messages)) {
        console.error('Invalid messages response structure', data)
        return { events: [], runStatus: null }
      }

      // Convert ag-ui messages to ChatEvent format
      const messages = data.messages
      const thinkingParts = data.thinking_parts || []
      const messageTimestamps = data.message_timestamps || {}
      const toolResults = collectToolResults(messages)
      return {
        events: createChatEvents(
          messages,
          toolResults,
          thinkingParts,
          messageTimestamps,
          data.message_attachments || {},
          data.message_intents || {}
        ),
        runStatus: data.run_status ?? null,
      }
    },
    enabled: !!session && !!conversationId,
    // Always stale: entering a conversation must sync with the server. A
    // detached run can start AND finish while the conversation isn't on
    // screen, and a leave-time refetch can even cache a pre-run snapshot
    // (run_status null) as "fresh" — with a staleTime, switching back would
    // trust that snapshot, never refetch, never poll. (refetchOnMount can't
    // fix this: a query-key switch on a live observer skips the mount path.)
    staleTime: 0,
    // A focus refetch only matters while a run works; otherwise it would
    // re-download the whole thread (inline attachments included) for nothing.
    refetchOnWindowFocus: (query) => query.state.data?.runStatus === 'running',
    // Self-terminating poll: while a detached run works, keep refetching; the
    // poll that sees a terminal status carries the final messages with it, so
    // no separate invalidation is needed when the run finishes.
    refetchInterval: (query) =>
      !suspendPolling && query.state.data?.runStatus === 'running'
        ? RUN_STATUS_POLL_INTERVAL_MS
        : false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) {
        return false
      }
      return failureCount < 2
    },
  })
}

export function useDeleteConversation(agentId: AgentId) {
  const { session } = useSession()
  const { organization } = useOrganization()
  const orgSlug = organization?.slug
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await session?.getToken()
      const response = await fetch(conversationUrl(agentId, id), {
        method: 'DELETE',
        headers: {
          Authorization: token || '',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.statusText}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.list(agentId, orgSlug),
      })
    },
    onError: () => {
      // The confirm dialog re-enables itself on failure; surface why so the
      // silent re-enable isn't mistaken for a no-op.
      toast.error('Failed to delete conversation. Please try again.')
    },
  })
}
